import {
    IEmailRecipient,
    IIncludeItemCardSetAPI,
    IIncludeItemCompanySalesRateAPI,
    IIncludeItemCurrencyAPI,
    IIncludeTypeAPI,
    IIncludeUserAlarmAPIGet,
    IItemAPI,
    IRelationShipElement,
    IRelationshipItem,
    IRequestMainItem,
} from './index';
import { Dayjs } from 'dayjs';

export enum BytesPerKb {
    HUMAN = '1000',
    IT = '1024',
}
export interface IAboutInfo {
    address?: string;
    email?: string;
    iban?: string;
    name?: string;
    phoneNumber?: string;
    registration?: string;
    swift?: string;
    vat?: string;
    website?: string;
}

export interface IProfileAboutAPI {
    about?: {
        id: number;
        address?: string;
        email?: string;
        website?: string;
        phonenumber?: string;
        c_registration?: string;
        iban?: string;
        swift?: string;
        vat?: string;
    };
}
export interface IAboutAPI {
    data?: {
        id: string;
        type: 'about';
        attributes?: {
            address?: string;
            email?: string;
            website?: string;
            phonenumber?: string;
            c_registration?: string;
            iban?: string;
            swift?: string;
            vat?: string;
        };
    };
}

export type ICompanyApiKeyList = ICompanyApiKeyItem[];

export interface ICompanyApiKeyItem {
    id: string;
    label: string;
    token?: string;
    createdAt: string;
}

export type ICompanyApiKeyListAPI = {
    api_keys: ICompanyApiKeyAPIItem[];
};

export interface ICompanyApiKeyAPIItem {
    id: string;
    label: string;
    token?: string;
    created_at: string;
}

export interface ICompanyForm extends ICompanyInfo {
    salesRateList: ICompanyRateList;
    operatorList: ICompanyOperatorList;
}

export type ICompanyInfo = ICompanyTenantConfig & {
    id: string;
    accountNumber?: string;
    addressBuilding?: string;
    addressNumber?: string;
    addressStreet?: string;
    balance?: string;
    billingCycleStartDay?: number;
    billingEmails?: Array<IEmailRecipient>;
    billingToDepartment?: string;
    cdrSquashInterval?: number;
    cdrSquashIntervalName?: string;
    cdrSquashIntervalType?: string;
    cdrSquashIntervalValue?: number;
    companyCode?: string;
    companyType?: string;
    companyTypeId?: string;
    countryId?: string;
    currency?: string;
    currencyId?: string;
    customAboutEnabled?: boolean;
    customerPrivacy?: boolean;
    customLogoLinkUrl?: string;
    darkLogoBlobId?: string;
    darkLogoUrl?: string;
    deletedAt?: boolean;
    externalId?: string;
    financialContactId?: string;
    geolocationEnabled?: boolean;
    geotrackingEnabled?: boolean;
    helpDeskEnabled?: boolean;
    iban?: string;
    invoicedByCompanyId?: string;
    invoiceFooter?: string;
    isHashed?: boolean;
    isTenantConfig?: boolean;
    logoBlobId?: string;
    logoUrl?: string;
    name?: string;
    notes?: string;
    overduePeriod?: number;
    parent?: string;
    parentId?: string;
    paymentTypeId?: string;
    paymentTypeName?: string;
    primaryContact?: string;
    primaryContactId?: string;
    privacyCdr?: boolean;
    sessionGrantedTypeId?: string;
    sessionGrantedUnit?: number;
    sessionValidityTime?: number;
    slug?: string;
    spendingLimit?: number;
    subscribersAmount?: number;
    subscribersCsvBlobId?: string;
    subscribersOnPremAmount?: number;
    swift?: string;
    technicalContactId?: string;
    thresholdHigh?: number;
    thresholdMedium?: number;
    vatNumber?: string;
    vatPercentage?: string;
    whiteLabel?: boolean;
    zeroCostData?: boolean;
    zeroCostSms?: boolean;
    zeroCostVoice?: boolean;
};

export interface IBlobIds {
    darkLogoBlobId?: string;
    logoBlobId?: string;
}

export interface ICompanyPostDataAttributesAPI {
    address_building?: string;
    address_number?: string;
    address_street?: string;
    bank_name?: string;
    billing_cycle_start_day?: number;
    billing_emails?: string;
    billing_to_department?: string;
    city?: string;
    company_type_id?: string;
    country_id?: string;
    credit_card_number?: string;
    credit_card_type?: string;
    currency_id?: string;
    custom_logo_link_url?: string;
    customer_privacy?: boolean;
    dark_logo_blob_id?: string;
    external_id?: string;
    financial_contact_id?: string;
    geolocation_enabled?: boolean;
    geotracking_enabled?: boolean;
    iban_bank_account_number?: string;
    invoiced_by_company_id?: string;
    logo_blob_id?: string;
    name?: string;
    name_of_credit_card?: string;
    notes?: string;
    parent_id?: string;
    payment_terms?: string;
    payment_type_id?: string;
    primary_contact_id?: string;
    privacy_cdr?: boolean;
    session_granted_type_id?: string;
    session_granted_volume_unit?: string;
    new_session_granted_unit?: number;
    session_validity_time?: number;
    slug?: string;
    subscribers_amount?: number;
    subscribers_csv_blob_id?: string;
    subscribers_on_prem_amount?: number;
    swift_bic_bank_code?: string;
    technical_contact_id?: string;
    vat_number?: string;
    vat_percentage?: string;
    whitelabel?: boolean;
    zip_code?: string;
}

export interface ICompanyIncludeRateAPI {
    id: string;
    type: IIncludeTypeAPI;
    attributes: {
        sales_rate_list_id?: string;
        start_date?: string;
        end_date?: string;
    };
}

export interface ICompanyIncludeOperatorPOSTAPI {
    id: string;
    type: IIncludeTypeAPI;
    attributes: {
        operator_id?: string;
    };
}

export interface IIncludeCompanyOperatorGETAPI {
    id: string;
    type: IIncludeTypeAPI;
    attributes: {
        [id: string]: string;
    };
    relationships: {
        operator?: {
            data?: IRelationShipElement;
        };
    };
}

export interface ICompanyGetAPI {
    data?: {
        id: string;
        attributes?: {
            name?: string;
            balance?: string;
            external_id?: string;
            billing_cycle_start_day?: number;
            billing_emails?: Array<IEmailRecipient>;
            cdr_squash_interval?: number;
            customer_privacy?: boolean;
            deleted_at?: boolean;
            notes?: string;
            payment_type?: string;
            privacy_cdr?: boolean;
            session_granted_volume_unit?: string;
            new_session_granted_unit?: number;
            session_validity_time?: number;
            spending_limit?: string;
            subscribers_amount?: number;
            subscribers_on_prem_amount?: number;
            threshold_high?: string;
            threshold_medium?: string;
        };
        relationships?: {
            company_parent?: {
                data?: IRelationshipItem;
            };
            financial_contact?: {
                data?: IRelationshipItem;
            };
            primary_contact?: {
                data?: IRelationshipItem;
            };
            technical_contact?: {
                data?: IRelationshipItem;
            };
            country?: {
                data?: IRelationshipItem;
            };
            currency?: {
                data?: IRelationshipItem;
            };
            company_type?: {
                data?: IRelationshipItem;
            };
            company_sales_rate_lists?: {
                data?: IRelationshipItem[];
            };
            users?: {
                data?: IRelationshipItem[];
            };
            card_sets?: {
                data?: IRelationshipItem[];
            };
            payment_type?: {
                data?: IRelationshipItem;
            };
            session_granted_type?: {
                data?: IRelationshipItem;
            };
            logo_blob?: {
                data?: IRelationshipItem;
            };
            invoice_configuration?: {
                data?: IRelationshipItem;
            };
            dark_logo_blob?: {
                data?: IRelationshipItem;
            };
            companies_operators?: {
                data?: Array<IRelationshipsCompanyOperatorItemAPI>;
            };
            tenant_config?: {
                data?: IRequestMainItem;
            };
        };
    };
    included?: Array<
        | IItemAPI
        | IIncludeItemCurrencyAPI
        | IIncludeItemCompanySalesRateAPI
        | IIncludeItemCardSetAPI
        | IIncludeCompanyOperatorGETAPI
    >;
}

export interface IRelationshipsCompanyOperatorItemAPI {
    id: string;
    type: IIncludeTypeAPI;
    destroy?: boolean;
    relationships?: {
        operator?: {
            data?: IRelationshipItem;
        };
    };
}

export type ICompanyOperatorList = Array<ICompanyOperator>;
export type ICompanyOperator = {
    id: string;
    destroy?: boolean;
    name?: string;
    operatorId?: string;
};

export type ICompanyRateList = ICompanyRateItem[];

export interface ICompanyRateItem {
    destroy?: boolean;
    endDate?: Dayjs;
    id: string;
    isActive?: boolean;
    isDeletable?: boolean;
    rateId?: string;
    rateName?: string;
    startDate?: Dayjs;
}

export type ICompanyCardSetsList = ICompanyCardSetsItem[];

export interface ICompanyCardSetsItem {
    id: string;
    name?: string;
    assignedCompanyId?: string;
    assignedCompany?: string;
    cardsCount?: string;
}

export type ICompanyStatistics = {
    id: string;
    companyId: string;
    balanceDifference?: string;
    balance?: string;
};

export type ICompanyCustomDocsTypes = 'PRIVACY_POLICY' | 'TERMS_AND_CONDITIONS';

export type ICompanyCustomDocs = {
    companyId: string;
    blobId: string;
    docType: ICompanyCustomDocsTypes;
    file: File;
};

export type ICompanySubscriptionsBody = {
    period: 'weekly' | 'monthly';
    amount_cents: number;
    orderable_type: string;
    orderable_id?: string;
};

export interface ICompanySubscriptionsGetAPI {
    user_subscriptions: ICompanySubscriptionsGetAPIItem[];
}

export interface ICompanySubscriptionsGetAPIItem {
    id: string;
    amount_cents: number;
    target_type: string;
    period: string;
    status: string;
    created_at: string;
    updated_at: string;
}

export interface ICompanySubscriptionInfo {
    id?: string;
    amountCents?: number;
    targetType?: string;
    period?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface IMonthlyRecurringChargesDiscount {
    description: string;
    amount: string;
    amount_type: string;
}

export type IMonthlyRecurringCharges = IMonthlyRecurringChargesItem[];
export interface IMonthlyRecurringChargesItem {
    id: number | string;
    chargeType?: 'imsi' | 'card';
    name: string;
    amountCents: number;
    discount?: IMonthlyRecurringChargesDiscount;
}

export interface IMonthlyRecurringChargesAPI {
    meta: {
        is_freezing_period: boolean;
    };
    monthly_recurring_charges: IMonthlyRecurringChargesAPIItem[];
}

export interface IMonthlyRecurringChargesAPIItem {
    id?: number;
    charge_type: 'imsi' | 'card' | null;
    name: string;
    amount_cents: number;
    discount?: IMonthlyRecurringChargesDiscount;
}

export type INonRecurringCharges = INonRecurringChargesItem[];
export interface INonRecurringChargesItem {
    id: number | string;
    name: string;
    amountCents: number;
    predefinedType?: string;
}

export interface INonRecurringChargesAPI {
    non_recurring_charges: INonRecurringChargesAPIItem[];
}

export interface IGeneralNonRecurringChargesAPI {
    general_non_recurring_charges: INonRecurringChargesAPIItem[];
}

export interface INonRecurringChargesAPIItem {
    id?: number;
    name: string;
    amount_cents: number;
    predefined_type: string;
}

export interface ICompanyHierarchyV2APIItem {
    id: number;
    name: string;
    children: ICompanyHierarchyV2APIItem[];
}

export const companyConfigCompanyTypesFields = [
    'notification_company_threshold_medium_reached',
    'notification_company_threshold_high_reached',
    'notification_company_balance_limit_reached',
] as const;
export const companyConfigCardIMEITypesFields = ['notification_card_imei_lock_violation'] as const;
export const companyConfigCardTypesFields = [
    'notification_card_threshold_50_reached',
    'notification_card_threshold_75_reached',
    'notification_card_threshold_100_reached',
    'suspend_card_threshold_100_reached',
] as const;
export const companyConfigCardSetTypesFields = [
    'notification_card_set_threshold_50_reached',
    'notification_card_set_threshold_75_reached',
    'notification_card_set_threshold_100_reached',
    'suspend_card_set_threshold_100_reached',
] as const;
export const companyConfigCardSetTotalTypesFields = [
    'notification_card_set_total_threshold_50_reached',
    'notification_card_set_total_threshold_75_reached',
    'notification_card_set_total_threshold_100_reached',
] as const;
export const companyConfigTypesFields = [
    ...companyConfigCompanyTypesFields,
    ...companyConfigCardIMEITypesFields,
    ...companyConfigCardTypesFields,
    ...companyConfigCardSetTypesFields,
    ...companyConfigCardSetTotalTypesFields,
] as const;
// export type ICompanyConfigCompanyTypes = (typeof companyConfigCompanyTypesFields)[number];
// export type ICompanyConfigCardIMEITypes = (typeof companyConfigCardIMEITypesFields)[number];
// export type ICompanyConfigCardTypes = (typeof companyConfigCardTypesFields)[number];
// export type ICompanyConfigCardSetTypes = (typeof companyConfigCardSetTypesFields)[number];
// export type ICompanyConfigCardSetTotalTypes = (typeof companyConfigCardSetTotalTypesFields)[number];
export type ICompanyConfigTypes = (typeof companyConfigTypesFields)[number];

export type ICompanyConfig = {
    id: string;
    alarmEmails?: Array<IEmailRecipient>;
    cardLimit?: number;
    cardSetIds?: string[];
    cardSetLimit?: number;
    cardSetRecordMap?: { [key: string]: string };
    cardSetTotalIds?: string[];
    cardSetTotalLimit?: number;
    mfa?: MfaType;
    mfaForcedBy?: string;
    // notification_card_imei_lock_violation?: boolean;
    // notification_card_set_threshold_100_reached?: boolean;
    // notification_card_set_threshold_50_reached?: boolean;
    // notification_card_set_threshold_75_reached?: boolean;
    // notification_card_threshold_100_reached?: boolean;
    // notification_card_threshold_50_reached?: boolean;
    // notification_card_threshold_75_reached?: boolean;
    // notification_company_balance_limit_reached?: boolean;
    // notification_company_threshold_high_reached?: boolean;
    // notification_company_threshold_medium_reached?: boolean;
    // suspend_card_set_threshold_100_reached?: boolean;
    // suspend_card_threshold_100_reached?: boolean;
} & {
    [K in ICompanyConfigTypes]?: boolean;
};

export type ICompanyConfigKeys = keyof ICompanyConfig;

export type IICompanyConfigTypesArr = ICompanyConfigTypes[];

export type ICompanyConfigAPIData = {
    id: string;
    type: 'company_config';
    attributes?: {
        events?: IICompanyConfigTypesArr;
        alarm_emails?: Array<IEmailRecipient>;
        card_limit?: number | null;
        card_set_limit?: number | null;
        mfa?: string;
        mfa_forced_by?: string;
    };
    relationships?: {
        card_sets?: {
            data?: Array<IRelationshipItem>;
        };
        company_configs_card_sets?: {
            data?: Array<IRelationshipItem>;
        };
    };
};

export interface ICompanyConfigAPI {
    data: ICompanyConfigAPIData;
    included?: Array<IIncludeUserAlarmAPIGet>;
}

export enum MfaType {
    CHILDREN = 'children',
    DISABLED = 'disabled',
    THIS = 'this',
    THIS_AND_CHILDREN = 'this_and_children',
}

export type ICompanyConfigNewAlarmAPI = {
    id?: number;
    alarm_emails?: IEmailRecipient[];
    card_limit?: string;
    card_set_ids?: number[];
    card_set_limit?: string;
    card_set_total_ids?: number[];
    card_set_total_limit?: string;
    events?: IICompanyConfigTypesArr;
    mfa?: MfaType;
    mfa_forced_by?: string;
};

export type IAssignedCardSets = {
    id: string;
    type: IIncludeTypeAPI;
    cardSetId: string;
    destroy: boolean;
    isAlreadyAssigned: boolean;
};

export type ICompanyTenantConfigAPI = {
    bytes_per_kb?: string;
    hide_costs?: boolean;
    require_tariff?: boolean;
    show_package_data?: boolean;
};
export type ICompanyTenantConfigIncludeAPI = ICompanyTenantConfigAPI & {
    id?: number;
    company_id?: number;
};

export type ICompanyTenantConfig = {
    bytesPerKb?: BytesPerKb;
    hideCosts?: boolean;
    requireTariff?: boolean;
    showPackageData?: boolean;
    tenantConfigId?: number;
};
export const defaultCompanyTenantConfig: ICompanyTenantConfig = {
    bytesPerKb: BytesPerKb.HUMAN,
    hideCosts: false,
    requireTariff: false,
    showPackageData: false,
    tenantConfigId: undefined,
};
